import { Component, Prop } from 'vue-property-decorator';
import InputMixin from '@/mixins/inputMixins/base/InputMixin';

//eslint-disable-next-line no-control-regex
const XML_10_INVALID_CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g;

// Unicode "space separator" characters (general category Zs), e.g. NO-BREAK SPACE
// (U+00A0). Visually indistinguishable from a regular space but render/compare
// inconsistently, so they are normalized to a regular space rather than removed.
const UNICODE_SPACE_SEPARATORS = /[\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g;

// Invisible/format Unicode characters (general category Cf, e.g. ZERO WIDTH SPACE
// U+200B, SOFT HYPHEN U+00AD, ZERO WIDTH NO-BREAK SPACE/BOM U+FEFF) and C1 control
// characters (U+0080-U+009F). These have no visible representation but pollute the
// stored data, so they are removed entirely.
//eslint-disable-next-line no-control-regex
const EXOTIC_INVISIBLE_CHARS = /[\u0080-\u009F\u00AD\u200B-\u200F\u202A-\u202E\u2060-\u2064\uFEFF]/g;

@Component
export default class TextInputMixin extends InputMixin {
  @Prop({ default: '' }) defaultValue: string;

  /**
   * Removes XML 1.0 invalid control characters and other "exotic" Unicode
   * characters from user input, and normalizes Unicode space separators
   * (e.g. NO-BREAK SPACE) to a regular space.
   *
   * These characters can be introduced through copy/paste operations (e.g. from
   * a PDF) and are invisible or render inconsistently to users, but may cause XML
   * serialization to fail during document generation or pollute stored data.
   *
   * Called on every keystroke (input event), so leading/trailing whitespace is
   * intentionally NOT trimmed here - trimming live would strip the space a user
   * just typed before they can continue typing the next word. Trimming happens
   * separately on blur, see trimInput.
   *
   * Allowed control characters:
   * - Tab (U+0009)
   * - Line Feed (U+000A)
   * - Carriage Return (U+000D)
   *
   * @param value The input value to sanitize.
   * @returns The sanitized value, or the original value if null or undefined.
   */
  protected sanitizeInput(value: string | null | undefined): string | null | undefined {
    if (value == null) {
      return value;
    }

    return value
      .replace(XML_10_INVALID_CONTROL_CHARS, '')
      .replace(UNICODE_SPACE_SEPARATORS, ' ')
      .replace(EXOTIC_INVISIBLE_CHARS, '');
  }

  /**
   * Trims leading/trailing whitespace from user input. Intended to be called
   * on blur, not on every keystroke (see sanitizeInput).
   *
   * @param value The input value to trim.
   * @returns The trimmed value, or the original value if null or undefined.
   */
  protected trimInput(value: string | null | undefined): string | null | undefined {
    if (value == null) {
      return value;
    }

    return value.trim();
  }
}
